/** Form Panosu — paylaşılan kart tipleri + KPI/hesap yardımcıları (backend form.dashboard ile ayna). */
import { type Formula, type Operand, formulaRefs } from "./formFormula";

export type DashCalc = "sum" | "avg" | "count" | "min" | "max" | "first" | "last";
export type DashChartType = "bar" | "line" | "area" | "donut";
export type DashKind = "kpi" | "chart";
export type DashGroupBy = "day" | "shift";

/** KPI kartı içindeki öğe türü. metric=form değeri, static=sabit sayı, formula=kart-içi dört işlem,
 *  note=bilgi notu, heading=başlık, divider=çizgi. */
export type DashItemType = "metric" | "static" | "formula" | "note" | "heading" | "divider";
export type DashAlign = "left" | "center" | "right";
/** Etiket-değer yerleşimi: stack=etiket üstte (varsayılan), row=etiket solda · değer sağda (yan yana). */
export type DashItemLayout = "stack" | "row";

/** Bir öğenin görünüm stili — kullanıcı özelleştirir. Boş/undefined → tema varsayılanı. */
export interface DashMetricStyle {
  fontSize?: number;   // px
  color?: string;      // hex (#rrggbb); boş → tema (--neutral-900)
  fontWeight?: number; // 400/500/600/700
  align?: DashAlign;   // değer/metin hizası (sol/orta/sağ)
  caps?: boolean;      // yalnız etiket: BÜYÜK HARF; undefined → yerleşime göre CSS varsayılanı
}
/** KPI kartı içindeki bir ÖĞE: form değeri (metric), sabit sayı (static), bilgi notu (note), başlık (heading) veya çizgi (divider). */
export interface DashMetric {
  id: string;
  type?: DashItemType;          // yok → "metric" (geriye-uyumlu)
  // metric (form değeri)
  formTemplateId?: string;
  fieldId?: string;
  calc?: DashCalc;
  // static (sabit sayı)
  value?: number;
  // formula (kart-içi hesap) — operandlar AYNI karttaki öğelerin id'sine referans verir (farklı form/tesis olabilir)
  formula?: Formula;
  // note / heading içeriği
  text?: string;
  // ortak
  label?: string;               // metric/static etiketi (boş → "alan · hesap")
  unit?: string;                // static/metric birimi (metric'te form biriminden gelir)
  layout?: DashItemLayout;      // metric/static: etiket-değer yerleşimi (yok → "stack")
  style?: DashMetricStyle | null;      // DEĞERİN stili
  labelStyle?: DashMetricStyle | null; // ETİKETİN stili (yok → yerleşime göre CSS varsayılanı)
}

/** Öğe metrik mi (form değeri üreten) — yalnız bunlar aggregate spec'i olur. */
export function isMetricItem(m: DashMetric): boolean {
  return (m.type ?? "metric") === "metric";
}
export const ITEM_TYPE_LABELS: Record<DashItemType, string> = { metric: "Metrik", static: "Sabit Sayı", formula: "Formül", note: "Bilgi Notu", heading: "Başlık", divider: "Çizgi" };
export const LAYOUT_OPTIONS: Array<{ value: DashItemLayout; label: string; hint: string }> = [
  { value: "stack", label: "Alt alta", hint: "Etiket üstte, değer altta" },
  { value: "row", label: "Yan yana", hint: "Etiket solda, değer sağda" },
];
export const ALIGN_OPTIONS: Array<{ value: DashAlign; label: string; icon: string }> = [
  { value: "left", label: "Sola", icon: "alignLeft" },
  { value: "center", label: "Ortala", icon: "alignCenter" },
  { value: "right", label: "Sağa", icon: "alignRight" },
];

export interface DashCard {
  id: string;
  kind: DashKind;
  title: string;
  titleStyle?: DashMetricStyle | null; // kart başlığının (.head .lbl) biçimi; yok → kanonik sınıf varsayılanı
  // Üst-seviye alanlar: grafik kartının kaynağı + KPI kartının geriye-uyumlu tek metriği (metrics[0]).
  formTemplateId: string;
  fieldId: string;
  calc: DashCalc;
  // KPI kartı: birden çok değer. Yoksa üst-seviye alanlardan tek metrik türetilir (eski kartlar).
  metrics?: DashMetric[];
  chartType?: DashChartType;
  groupBy?: DashGroupBy;
  from?: string;
  to?: string;
  // Serbest 2D ızgara konumu (Faz 3 sürükle-bırak). x=kolon(0-11), y=satır, w=genişlik(kolon), h=yükseklik(satır).
  x: number;
  y: number;
  w: number;
  h: number;
}

export const FONT_WEIGHTS: Array<{ value: number; label: string }> = [
  { value: 400, label: "Normal" },
  { value: 500, label: "Orta" },
  { value: 600, label: "Yarı Kalın" },
  { value: 700, label: "Kalın" },
];
export const DEFAULT_METRIC_FONT_SIZE = 24;
export const DEFAULT_METRIC_FONT_WEIGHT = 700;
/** Kart başlığı varsayılanı — blocks.css `.dash-kpi .head .lbl` ile ayna (10.5px → editörde tam sayı 11). */
export const TITLE_DEFAULTS = { fontSize: 11, fontWeight: 600, caps: true };
/** Etiket varsayılanları — CSS ile ayna: stack 10.5px/600/BÜYÜK, row 13px/500/normal (editörde tam sayı). */
export const LABEL_DEFAULTS: Record<DashItemLayout, { fontSize: number; fontWeight: number; caps: boolean }> = {
  stack: { fontSize: 11, fontWeight: 600, caps: true },
  row: { fontSize: 13, fontWeight: 500, caps: false },
};

/** Kartın metrikleri: metrics doluysa onu, yoksa üst-seviye tek alandan GERİYE-UYUMLU tek metrik. */
export function cardMetrics(card: DashCard): DashMetric[] {
  if (card.metrics && card.metrics.length > 0) return card.metrics;
  return [{ id: "m0", type: "metric", formTemplateId: card.formTemplateId, fieldId: card.fieldId, calc: card.calc, style: null }];
}
/** Bir metriğin aggregate spec anahtarı (kart+metrik benzersiz). */
export function metricSpecKey(cardId: string, metricId: string): string { return `${cardId}::${metricId}`; }

/** Değer üreten öğe mi (metrik / sabit sayı / formül) — yapısal öğeler (not, başlık, çizgi) hariç. */
export function isValueItem(m: DashMetric): boolean {
  const t = m.type ?? "metric";
  return t === "metric" || t === "static" || t === "formula";
}

/**
 * Kart öğelerinin sayısal değerleri: metric=aggregate sonucu, static=sabit, formula=kart-içi hesap.
 * Formül operandları AYNI karttaki öğelerin id'sine referans verir (öğeler farklı form/tesisten olabilir).
 * Bağımlılıklar iteratif çözülür (formül başka formüle bağlanabilir); döngü / eksik değer / 0'a bölme → null.
 * Semantik formFormula.computeFormulaValues ile birebir (soldan-sağa, 6 ondalık yuvarlama).
 */
export function computeItemValues(items: DashMetric[], baseValue: (m: DashMetric) => number | null): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const byId = new Map(items.map((m) => [m.id, m]));
  const pending = new Set<string>();
  for (const m of items) {
    if ((m.type ?? "metric") === "formula") pending.add(m.id);
    else if (isValueItem(m)) out[m.id] = baseValue(m);
  }
  const operandVal = (o: Operand): number | null => {
    if (o.kind === "const") return Number.isFinite(o.value) ? o.value : null;
    if (!byId.has(o.ref)) return null;      // silinmiş öğeye referans
    return out[o.ref] ?? null;
  };
  const evalFormula = (f: Formula): number | null => {
    let acc = operandVal(f.start);
    if (acc == null) return null;
    for (const step of f.steps) {
      const b = operandVal(step.operand);
      if (b == null) return null;
      if (step.op === "+") acc += b;
      else if (step.op === "-") acc -= b;
      else if (step.op === "*") acc *= b;
      else { if (b === 0) return null; acc /= b; }
    }
    return Number.isFinite(acc) ? Math.round(acc * 1e6) / 1e6 : null;
  };
  let progress = true;
  while (progress && pending.size) {
    progress = false;
    for (const m of items) {
      if (!pending.has(m.id)) continue;
      const f = m.formula;
      if (!f) { out[m.id] = null; pending.delete(m.id); progress = true; continue; }
      // Yalnız TÜM operandları çözülmüş formülü hesapla (sırasız tanım desteklenir).
      if (!formulaRefs(f).every((ref) => !pending.has(ref))) continue;
      out[m.id] = evalFormula(f);
      pending.delete(m.id);
      progress = true;
    }
  }
  for (const id of pending) out[id] = null; // döngü → null
  return out;
}

let metricCounter = 0;
export function newMetricId(): string {
  try { return "m-" + crypto.randomUUID().slice(0, 6); } catch { return `m-${Date.now().toString(36)}-${++metricCounter}`; }
}

export const CALC_LABELS: Record<DashCalc, string> = {
  sum: "Toplam",
  avg: "Ortalama",
  count: "Adet",
  min: "En Düşük",
  max: "En Yüksek",
  first: "İlk Değer",
  last: "Son Değer",
};
export const CALC_OPTIONS: DashCalc[] = ["sum", "avg", "count", "max", "min", "last", "first"];

export const CHART_LABELS: Record<DashChartType, string> = { bar: "Çubuk", line: "Çizgi", area: "Alan", donut: "Halka" };
export const GROUPBY_LABELS: Record<DashGroupBy, string> = { day: "Güne göre", shift: "Vardiyaya göre" };

/** KPI/eksen değerini TR formatla (null → "—"). */
export function fmtKpi(v: number | null | undefined, maxFrac = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: maxFrac }).format(v);
}

/** Kart index'ine göre tasarım-sistemi kategori rengi (--chart-1..8). Okunur sırayla (koyu/nötrler sonda). */
const CHART_ORDER = [1, 3, 4, 5, 6, 7, 2, 8];
export function chartColor(i: number): string {
  return `var(--chart-${CHART_ORDER[i % CHART_ORDER.length]})`;
}

let cardCounter = 0;
export function newCardId(): string {
  try { return "dc-" + crypto.randomUUID().slice(0, 8); } catch { return `dc-${Date.now().toString(36)}-${++cardCounter}`; }
}

/** Chart serisi noktası (backend form.dashboard.series çıktısı). */
export interface ChartPoint { label: string; value: number | null; count: number }

/** Eksen etiketi kısalt: YYYY-MM-DD → GG.AA; uzun ad → kırp. */
export function shortLabel(l: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(l);
  if (m) return `${m[3]}.${m[2]}`;
  return l.length > 9 ? l.slice(0, 8) + "…" : l;
}
